/**
 * CountrySelector
 *
 * Header dropdown that lets users switch between supported countries/locales.
 * Shows the current country flag + code, and expands into a dropdown listing
 * all active countries with flag, name, and currency.
 */

import styled from '@emotion/styled';
import { ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useIntl, getCountryGroups, getLanguageLabel } from '@/lib/intl';
import { useTranslation } from '@/lib/i18n';
import {
  COUNTRY_SELECTOR_ACCESSIBILITY_COUNTRY_LIST,
  COUNTRY_SELECTOR_ACCESSIBILITY_SELECT,
  COUNTRY_SELECTOR_LABEL_COUNTRY_CURRENCY,
} from './i18.keys';
import type { CountryConfig } from '@template-project/storefront-shared';

/**
 * Convert an ISO 3166-1 alpha-2 country code to its flag emoji.
 * Works by mapping each letter to a Regional Indicator Symbol.
 * e.g. "US" -> "\uD83C\uDDFA\uD83C\uDDF8" (flag-us)
 */
export function countryCodeToFlagEmoji(code: string): string {
  return [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 - 65 + (c.codePointAt(0) ?? 0)))
    .join('');
}

const SelectorWrapper = styled.div<{ $drawer?: boolean }>`
  position: relative;
  display: ${({ $drawer }) => ($drawer ? 'flex' : 'inline-flex')};
  flex-direction: column;
  width: ${({ $drawer }) => ($drawer ? '100%' : 'auto')};
`;

const TriggerButton = styled.button<{ $drawer?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
  background: none;
  border: ${({ $drawer, theme }) => ($drawer ? 'none' : `1px solid ${theme.colors.border.color}`)};
  border-radius: ${({ theme, $drawer }) => ($drawer ? 0 : theme.radius.small)};
  padding: ${({ $drawer, theme }) =>
    $drawer ? `${theme.space.sm} 0` : `${theme.space.xs} ${theme.space.sm}`};
  cursor: pointer;
  font-size: ${({ theme, $drawer }) => theme.fontSizes[$drawer ? 4 : 1]}px;
  font-weight: ${({ theme, $drawer }) => ($drawer ? theme.typography.config.weight.demi : theme.typography.config.weight.regular)};
  color: ${({ theme }) => theme.colors.text.primary};
  transition: border-color 0.2s, background-color 0.2s, opacity 0.2s;
  white-space: nowrap;
  width: ${({ $drawer }) => ($drawer ? '100%' : 'auto')};
  justify-content: ${({ $drawer }) => ($drawer ? 'space-between' : 'flex-start')};

  &:hover {
    ${({ $drawer, theme }) =>
      $drawer
        ? 'opacity: 0.7;'
        : `
      border-color: ${theme.colors.text.secondary};
      background-color: ${theme.colors.background.secondary};
    `}
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.pallete.primary.color};
    outline-offset: 2px;
  }
`;

const FlagSpan = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const ChevronWrapper = styled.span<{ readonly open: boolean }>`
  display: inline-flex;
  align-items: center;
  transition: transform 0.2s;
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0)')};
`;

const Dropdown = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 220px;
  max-height: min(60vh, 420px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  list-style: none;
  margin: 0;
  padding: 4px 0;
  z-index: 1100;
`;

/** In-flow list for mobile nav drawer — grows scroll height so all countries are reachable. */
const DrawerCountryList = styled.ul`
  list-style: none;
  margin: ${({ theme }) => theme.space.xs} 0 0;
  padding: 4px 0;
  width: 100%;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.radius.regular};
`;

const DropdownItem = styled.li<{ readonly active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ active, theme }) => (active ? theme.colors.background.secondary : 'transparent')};
  font-weight: ${({ active }) => (active ? 600 : 400)};
  transition: background-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`;

const CountryInfo = styled.span`
  flex: 1;
`;

const CurrencyBadge = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LanguageLinks = styled.div`
  display: flex;
  gap: 6px;
  font-size: 11px;
  margin-top: 2px;
`;

const LangLink = styled.button<{ readonly active: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 11px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  color: ${({ active, theme }) =>
    active ? theme.colors.text.primary : theme.colors.text.secondary};
  text-decoration: ${({ active }) => (active ? 'none' : 'underline')};
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const LangSeparator = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 11px;
`;

export interface CountrySelectorProps {
  /** `drawer`: inline expandable list for mobile nav (scrolls with menu). */
  variant?: 'default' | 'drawer';
}

export function CountrySelector({ variant = 'default' }: Readonly<CountrySelectorProps>) {
  const isDrawer = variant === 'drawer';
  const { locale, countryConfig, changeLocale } = useIntl();
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const countryGroups = getCountryGroups();

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleSelect = useCallback(
    (config: CountryConfig) => {
      if (config.locale !== locale) {
        changeLocale(config.locale);
      }
      setOpen(false);
    },
    [locale, changeLocale]
  );

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const countryList = (
    <>
      {countryGroups.map((group) => {
        const isActiveCountry = countryConfig.countryCode === group.countryCode;
        const primaryLocale = group.locales[0];
        const hasMultipleLanguages = group.locales.length > 1;

        return (
          <DropdownItem
            key={group.countryCode}
            active={isActiveCountry}
            role="option"
            aria-selected={isActiveCountry}
            onClick={hasMultipleLanguages ? undefined : () => handleSelect(primaryLocale)}
            style={hasMultipleLanguages ? { cursor: 'default' } : undefined}
          >
            <FlagSpan>{countryCodeToFlagEmoji(group.countryCode)}</FlagSpan>
            <CountryInfo>
              {group.displayName}
              {hasMultipleLanguages && (
                <LanguageLinks>
                  {group.locales.map((loc, idx) => (
                    <span key={loc.locale}>
                      {idx > 0 && <LangSeparator> · </LangSeparator>}
                      <LangLink
                        active={loc.locale === locale}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(loc);
                        }}
                        type="button"
                      >
                        {getLanguageLabel(loc.language)}
                      </LangLink>
                    </span>
                  ))}
                </LanguageLinks>
              )}
            </CountryInfo>
            <CurrencyBadge>{group.currency.code}</CurrencyBadge>
          </DropdownItem>
        );
      })}
    </>
  );

  return (
    <SelectorWrapper ref={wrapperRef} $drawer={isDrawer}>
      <TriggerButton
        $drawer={isDrawer}
        onClick={handleToggle}
        aria-label={t(COUNTRY_SELECTOR_ACCESSIBILITY_SELECT)}
        aria-expanded={open}
        aria-haspopup="listbox"
        type="button"
      >
        <FlagSpan>{countryCodeToFlagEmoji(countryConfig.countryCode)}</FlagSpan>
        {isDrawer ? t(COUNTRY_SELECTOR_LABEL_COUNTRY_CURRENCY) : countryConfig.countryCode}
        <ChevronWrapper open={open}>
          <ChevronDown size={12} aria-hidden />
        </ChevronWrapper>
      </TriggerButton>

      {open && isDrawer && (
        <DrawerCountryList role="listbox" aria-label={t(COUNTRY_SELECTOR_ACCESSIBILITY_COUNTRY_LIST)}>
          {countryList}
        </DrawerCountryList>
      )}

      {open && !isDrawer && (
        <Dropdown role="listbox" aria-label={t(COUNTRY_SELECTOR_ACCESSIBILITY_COUNTRY_LIST)}>
          {countryList}
        </Dropdown>
      )}
    </SelectorWrapper>
  );
}
